import React, { useMemo, useState } from "react";
import ControlsTable from "./ControlsTable";
import { useControls, usePatchControl } from "./useComplianceData";

export default function FrameworkDetail({ customerId, framework, onBack, owners, state, setState }) {
  const [categoryFilter, setCategoryFilter] = useState(state.category || "");
  const [pageSize, setPageSize] = useState(12);

  const filters = useMemo(() => ({
    framework: framework.id,
    status: state.tab === "all" ? "" : state.tab === "pending" ? "pending" : state.tab === "implemented" ? "implemented" : "",
    owner: state.tab === "assigned" ? "assigned" : state.tab === "unassigned" ? "unassigned" : "",
    category: categoryFilter,
    search: state.search || "",
    flag: state.flag || "",
    page: state.page || 1,
    pageSize,
  }), [framework.id, state.tab, categoryFilter, state.search, state.flag, state.page, pageSize]);

  const { results, count, total_pages, tab_counts, loading, error, reload } = useControls(customerId, filters);
  const { patch } = usePatchControl(customerId);

  const displayedControls = results;

  const categories = useMemo(() => {
    const set = new Set();
    results.forEach((c) => set.add(c.category));
    return Array.from(set);
  }, [results]);

  const ringPct = framework.totalControls ? Math.round((framework.implemented / framework.totalControls) * 100) : 0;

  const onPatch = async (controlId, body) => {
    try {
      await patch(controlId, body);
      await reload();
      return true;
    } catch (e) {
      return false;
    }
  };

  return (
    <div className="space-y-6">
      <button type="button" onClick={onBack} className="text-sm font-medium text-indigo-600 hover:text-indigo-800">
        ← Back
      </button>

      {/* Header card */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 flex flex-col sm:flex-row gap-6">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className="w-8 h-8 rounded-lg bg-indigo-50 text-indigo-700 font-semibold flex items-center justify-center text-xs">
              {framework.shortName}
            </span>
            <h2 className="text-lg font-semibold text-gray-900">{framework.name}</h2>
          </div>
          <p className="text-sm text-gray-500 mb-3">{framework.description}</p>
          <div className="flex flex-wrap gap-2 mb-4">
            {framework.version && (
              <span className="text-xs bg-gray-100 text-gray-700 rounded-full px-3 py-1">{framework.version}</span>
            )}
            <span className="text-xs bg-gray-100 text-gray-700 rounded-full px-3 py-1">
              {framework.enabled ? "Enabled" : "Disabled"}
            </span>
            <span className="text-xs bg-gray-100 text-gray-700 rounded-full px-3 py-1">
              {framework.totalControls} controls
            </span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 text-xs">
            <Metric label="Implemented" value={framework.implemented} />
            <Metric label="In progress" value={framework.inProgress} />
            <Metric label="Not started" value={framework.notStarted} />
            <Metric label="Unassigned" value={framework.unassigned} />
            <Metric label="Overdue" value={framework.overdue} tone="text-red-600" />
          </div>
        </div>

        <div className="flex flex-col items-center justify-center flex-shrink-0">
          <div
            className="w-24 h-24 rounded-full flex items-center justify-center text-lg font-bold text-gray-900"
            style={{
              background: `conic-gradient(#22c55e ${ringPct * 3.6}deg, #e2e8f0 0deg)`,
            }}
          >
            <div className="w-16 h-16 rounded-full bg-white flex items-center justify-center">
              {ringPct}%
            </div>
          </div>
          <span className="text-xs text-gray-500 mt-2">Implemented</span>
        </div>
      </div>

      {/* Categories */}
      {categories.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {categories.map((cat) => {
            const inCat = results.filter((c) => c.category === cat);
            const implemented = inCat.filter((c) => c.status === "implemented").length;
            const active = categoryFilter === cat;
            return (
              <button
                key={cat}
                type="button"
                onClick={() => setCategoryFilter(active ? "" : cat)}
                aria-pressed={active}
                className={`text-left bg-white rounded-lg border p-4 transition-colors ${
                  active ? "border-indigo-400 ring-1 ring-indigo-200" : "border-gray-200 hover:border-indigo-200"
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-900">{cat}</span>
                  <span className="text-xs text-gray-500">{implemented}/{inCat.length}</span>
                </div>
                <div className="h-1.5 w-full rounded-full bg-slate-100 overflow-hidden">
                  <div
                    className="h-full bg-green-500"
                    style={{ width: `${inCat.length ? (implemented / inCat.length) * 100 : 0}%` }}
                  />
                </div>
              </button>
            );
          })}
        </div>
      )}

      <ControlsTable
        controls={displayedControls}
        loading={loading}
        error={error}
        count={count}
        page={state.page || 1}
        pageSize={pageSize}
        totalPages={total_pages}
        tabCounts={tab_counts}
        tab={state.tab}
        onTabChange={(tab) => setState((s) => ({ ...s, tab, page: 1 }))}
        search={state.search || ""}
        onSearchChange={(search) => setState((s) => ({ ...s, search, page: 1 }))}
        category={categoryFilter}
        onCategoryChange={(c) => setCategoryFilter(c)}
        categories={categories}
        flag={state.flag}
        onClearFlag={() => setState((s) => ({ ...s, flag: "", page: 1 }))}
        owners={owners}
        onPageChange={(nextPage, nextSize) => {
          if (nextSize && nextSize !== pageSize) {
            setPageSize(nextSize);
            setState((s) => ({ ...s, page: 1 }));
          } else {
            setState((s) => ({ ...s, page: nextPage }));
          }
        }}
        onPatch={onPatch}
        onClearFilters={() => {
          setCategoryFilter("");
          setState((s) => ({ ...s, tab: "all", search: "", flag: "", page: 1 }));
        }}
      />
    </div>
  );
}

function Metric({ label, value, tone = "text-gray-900" }) {
  return (
    <div className="bg-gray-50 rounded-lg p-2 text-center">
      <div className={`text-sm font-semibold ${tone}`}>{value}</div>
      <div className="text-[10px] text-gray-500">{label}</div>
    </div>
  );
}
